<template>
  <div
    v-tooltip="{
      content: tooltip,
      distance: 12,
    }"
    class="action-container"
  >
    <div v-if="showRequiredSteps" class="required-steps-container">
      <FriedParagraph
        class="required-steps-text hidden-xs-and-down"
        :data-external="`required-step-${requiredStepsMessage}-message`"
      >
        {{ requiredStepsMessage }}
      </FriedParagraph>
      <FriedH5
        class="required-steps-text hidden-xs-and-up"
        :data-external="`required-step-${requiredStepsMessage}-message`"
      >
        {{ requiredStepsMessage }}
      </FriedH5>
      <ThemedButton
        class="sign-action-button"
        :data-external="`required-steps-${requiredStepsButtonText}-btn`"
        :centered="true"
        :theme="theme"
        @click="showRequiredStep(nextRequiredId)"
      >
        {{ requiredStepsButtonText }}
      </ThemedButton>
    </div>
    <ThemedButton
      v-else-if="showSignButton"
      class="sign-action-button sign-button"
      :data-external="`signing-page-bottom-${signButtonText}-button`"
      :loading-recipient-input="loadingRecipientInput"
      :centered="true"
      :theme="theme"
      @click="sign"
    >
      {{ signButtonText }}
    </ThemedButton>
    <FriedParagraph v-else class="footer-message" data-external="signing-page-footer-message">
      {{ message }}
    </FriedParagraph>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { onMounted, computed, defineComponent } from 'vue';
import ThemedButton from '@getaccept/lib-shared/src/components/ThemedButton.vue';
import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/enums/recipient-status';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import { SigningHelper } from '@getaccept/lib-shared/src/signature/helpers/signing.helper';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { SigningPageDocument } from '@getaccept/lib-shared/src/types/signing-page-document';
import { Placement } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import { RecipientsHelper } from '@getaccept/lib-shared/src/recipients/helpers/recipients.helper';

export default defineComponent({
  components: {
    ThemedButton,
  },
  props: {
    document: {
      type: Object as PropType<SigningPageDocument>,
      required: true,
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    requiredIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    nextRequiredId: {
      type: String,
      default: '',
    },
    focusedRequiredId: {
      type: String,
      default: '',
    },
    theme: {
      type: Object as PropType<SigningTheme>,
      required: true,
    },
    isSigningEnabled: {
      type: Boolean,
      required: true,
    },
    startEid: {
      type: Boolean,
      required: true,
    },
    loadingRecipientInput: {
      type: Boolean,
      default: false,
    },
    hasFormInputFieldsWithErrors: {
      type: Boolean,
    },
  },
  emits: ['updateFocusedRequiredId', 'sign', 'approve'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const recipientIsSigner = computed(() => props.recipient.role === RecipientRole.Signer);

    const showRequiredSteps = computed(
      () =>
        (!hasRecipientSigned.value && hasRequiredSteps.value) || props.hasFormInputFieldsWithErrors
    );

    const hasRecipientSigned = computed(() =>
      SigningHelper.hasRecipientSigned(props.recipient.status)
    );

    const hasRequiredSteps = computed(() => props.requiredIds.length > 0 && !!props.nextRequiredId);

    const requiredStepsButtonText = computed(() =>
      props.focusedRequiredId ? t('next') : t('show-required')
    );

    const requiredStepsMessage = computed(() =>
      props.requiredIds.length === 1 ? t('you-have-a-required-step') : t('you-have-required-steps')
    );

    const showSignButton = computed(
      () =>
        [RecipientStatus.Viewed, RecipientStatus.Reviewed].includes(props.recipient.status) &&
        props.isSigningEnabled
    );

    const signButtonText = computed(() => {
      if (recipientIsSigner.value) {
        return props.theme?.signButtonText || t('sign-document');
      }
      return t('approve-document');
    });

    const message = computed(() => {
      if (props.recipient.role === RecipientRole.CC) {
        return '';
      }
      if (props.document.status === DocumentStatus.Signed) {
        return t('document-is-already-signed');
      }
      if (recipientIsSigner.value && hasRecipientSigned.value) {
        return t('has-already-signed-document');
      }
      if (RecipientsHelper.isApprover(props.recipient.role) && hasRecipientSigned.value) {
        return t('has-already-approved');
      }
      if (!props.isSigningEnabled) {
        return t('waiting-for-other-signers');
      }
      return '';
    });

    const tooltip = computed(() => {
      if (props.loadingRecipientInput) {
        return t('loading-recipient-input-signing-disabled');
      } else {
        return null;
      }
    });

    const showRequiredStep = (nextRequiredId: string) => {
      if (!nextRequiredId) {
        return;
      }

      const nextElement: HTMLElement = document.getElementById(props.nextRequiredId);
      if (!nextElement) {
        return;
      }
      nextElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      emit('updateFocusedRequiredId', props.nextRequiredId);
    };

    const sign = () => {
      recipientIsSigner.value ? emit('sign') : emit('approve');
    };

    onMounted(() => {
      if (props.startEid) {
        emit('sign');
      }
    });

    return {
      DocumentStatus,
      RecipientStatus,
      RecipientRole,
      recipientIsSigner,
      showRequiredSteps,
      hasRecipientSigned,
      hasRequiredSteps,
      requiredStepsButtonText,
      requiredStepsMessage,
      showSignButton,
      signButtonText,
      message,
      tooltip,
      showRequiredStep,
      sign,
      Placement,
    };
  },
});
</script>

<style lang="scss" scoped>
.action-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  .required-steps-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: var(--spacing-100);

    @media screen and (max-width: $xs) {
      flex-direction: column;
      gap: var(--spacing-50);
    }

    .required-steps-text {
      width: fit-content;
    }
  }

  .sign-action-button {
    max-width: 15rem;
    width: 100%;
  }

  .sign-button {
    @media screen and (max-width: $xs) {
      max-width: 100%;
    }
  }

  .footer-message {
    width: 100%;
    text-align: center;
  }
}
</style>
